'use client'

/* eslint-disable @typescript-eslint/no-explicit-any, max-lines-per-function, complexity */
// Approval workflow requires flexible data handling and complex state management

import { useState, useCallback, ReactNode, forwardRef } from 'react'
import { CheckCircle, XCircle, AlertTriangle, RotateCcw } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

/**
 * BIRHAUS Approval Workflow Component
 * 
 * Universal approval workflow implementing BIRHAUS principles:
 * - Two-step approval process (select → confirm)
 * - Spanish-first labels and messages
 * - Progressive disclosure (hide confirmation until action selected)
 * - Undo-over-confirm pattern (clear action without confirmation)
 * - Accessibility-first design (WCAG AA+)
 * - Generic item rendering for any approval scenario
 * - Cognitive load optimization (4-3-1 rule: max 2 primary actions)
 */

// BIRHAUS: Utility function (self-contained)
function cn(...classes: (string | string[] | undefined | null | false)[]): string {
  return classes.flat().filter(Boolean).join(' ')
}

export interface BirhausApprovalAction {
  key: string
  labelEs: string
  labelEn?: string
  variant: 'primary' | 'secondary' | 'danger'
  icon?: LucideIcon
  requiresReason?: boolean
  confirmationMessageEs?: string
  confirmationMessageEn?: string
}

export interface BirhausApprovalWorkflowProps<T> {
  // Core data
  item: T
  
  // Rendering
  renderItem: (item: T) => ReactNode
  
  // Actions (BIRHAUS 4-3-1 rule: max 2 primary actions typical for approval)
  actions?: BirhausApprovalAction[]
  
  // Event handlers
  onAction: (action: string, item: T, reason?: string) => Promise<void>
  onCancel?: () => void
  
  // BIRHAUS Principle #7: Spanish-first labels
  titleEs?: string
  titleEn?: string
  
  reasonLabelEs?: string
  reasonLabelEn?: string
  
  reasonPlaceholderEs?: string
  reasonPlaceholderEn?: string
  
  cancelButtonEs?: string
  cancelButtonEn?: string
  
  confirmButtonEs?: string
  confirmButtonEn?: string
  
  // State
  loading?: boolean
  error?: string
  
  // Locale
  locale?: 'es' | 'en'
  
  // Styling
  className?: string
  itemClassName?: string
  actionsClassName?: string
}

export const BirhausApprovalWorkflow = forwardRef<HTMLDivElement, BirhausApprovalWorkflowProps<any>>(({
  // Core
  item,
  renderItem,
  actions = [],
  
  // Events
  onAction,
  onCancel,
  
  // Labels
  titleEs = 'Revisar Elemento',
  titleEn = 'Review Item',
  
  reasonLabelEs = 'Comentarios',
  reasonLabelEn = 'Comments',
  
  reasonPlaceholderEs = 'Agregar comentarios...',
  reasonPlaceholderEn = 'Add comments...',
  
  cancelButtonEs = 'Cancelar',
  cancelButtonEn = 'Cancel',
  
  confirmButtonEs = 'Confirmar',
  confirmButtonEn = 'Confirm',
  
  // State
  loading = false,
  error,
  
  // Locale
  locale = 'es',
  
  // Styling
  className,
  itemClassName,
  actionsClassName,
  
  ...props
}, ref) => {
  const [selectedAction, setSelectedAction] = useState<string | null>(null)
  const [reason, setReason] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)

  // BIRHAUS Principle #7: Spanish-first text resolution
  const getDisplayText = useCallback((
    spanish: string,
    english?: string
  ): string => {
    return locale === 'es' ? spanish : (english || spanish)
  }, [locale])

  // BIRHAUS Validation: Check 4-3-1 cognitive rule
  if (actions.length > 4) {
    console.warn(
      `BIRHAUS ApprovalWorkflow: ${actions.length} actions exceed 4-3-1 cognitive rule recommendation of 4. ` +
      'Consider consolidating actions or using progressive disclosure.'
    )
  }

  // Get current action details
  const currentAction = selectedAction ? actions.find(a => a.key === selectedAction) : null

  // Handle action selection (BIRHAUS: Progressive disclosure)
  const handleActionSelect = useCallback((actionKey: string) => {
    setSelectedAction(actionKey)
    setReason('')
  }, [])

  // Handle confirmation (BIRHAUS: Async action handling)
  const handleConfirm = useCallback(async () => {
    if (!selectedAction || !currentAction) return

    // Validate required reason
    if (currentAction.requiresReason && !reason.trim()) {
      return // Could set error state here
    }

    try {
      setIsProcessing(true)
      await onAction(selectedAction, item, reason.trim() || undefined)
    } catch (error) {
      console.error('Approval action failed:', error)
      // Error handling could be enhanced here
    } finally {
      setIsProcessing(false)
    }
  }, [selectedAction, currentAction, reason, onAction, item])

  // Handle cancel/reset (BIRHAUS: Undo pattern)
  const handleCancel = useCallback(() => {
    setSelectedAction(null)
    setReason('')
    onCancel?.()
  }, [onCancel])

  // Reset to action selection (BIRHAUS: Undo pattern)
  const handleReset = useCallback(() => {
    setSelectedAction(null)
    setReason('')
  }, [])

  const displayTitle = getDisplayText(titleEs, titleEn)

  return (
    <div
      ref={ref}
      className={cn('birhaus-approval-workflow space-y-6', className)}
      {...props}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900">
          {displayTitle}
        </h2>
      </div>

      {/* Error Display */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Item Display */}
      <div className={cn(
        'bg-gray-50 border border-gray-200 rounded-lg p-6',
        itemClassName
      )}>
        {renderItem(item)}
      </div>

      {/* Action Selection or Confirmation */}
      {!selectedAction ? (
        // BIRHAUS: Primary action selection (4-3-1 rule compliant)
        <div className={cn('space-y-4', actionsClassName)}>
          <div className="flex flex-wrap gap-3">
            {actions.map((action) => {
              const Icon = action.icon
              const actionLabel = getDisplayText(action.labelEs, action.labelEn)
              
              return (
                <button
                  key={action.key}
                  type="button"
                  onClick={() => handleActionSelect(action.key)}
                  disabled={loading}
                  className={cn(
                    'inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg',
                    'border transition-all duration-200 flex-1 md:flex-none',
                    'focus:outline-none focus:ring-2 focus:ring-offset-1',
                    'disabled:opacity-50 disabled:cursor-not-allowed',
                    
                    // Variant styles
                    action.variant === 'primary' && [
                      'bg-blue-600 text-white border-blue-600',
                      'hover:bg-blue-700 hover:border-blue-700',
                      'focus:ring-blue-500'
                    ],
                    action.variant === 'secondary' && [
                      'bg-white text-gray-700 border-gray-300',
                      'hover:bg-gray-50 hover:border-gray-400',
                      'focus:ring-gray-500'
                    ],
                    action.variant === 'danger' && [
                      'bg-red-600 text-white border-red-600',
                      'hover:bg-red-700 hover:border-red-700',
                      'focus:ring-red-500'
                    ]
                  )}
                >
                  {Icon && <Icon className="w-4 h-4 mr-2" />}
                  {actionLabel}
                </button>
              )
            })}
          </div>
          
          {/* Cancel option */}
          {onCancel && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleCancel}
                disabled={loading}
                className={cn(
                  'text-sm text-gray-500 hover:text-gray-700',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              >
                {getDisplayText(cancelButtonEs, cancelButtonEn)}
              </button>
            </div>
          )}
        </div>
      ) : (
        // BIRHAUS: Confirmation step (Progressive disclosure)
        <div className="space-y-4">
          {/* Confirmation Message */}
          {currentAction && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-amber-900">
                    {getDisplayText(
                      currentAction.confirmationMessageEs || `Confirmar: ${currentAction.labelEs}`,
                      currentAction.confirmationMessageEn || `Confirm: ${currentAction.labelEn || currentAction.labelEs}`
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Reason/Comment Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {getDisplayText(reasonLabelEs, reasonLabelEn)}
              {currentAction?.requiresReason && (
                <span className="text-red-500 ml-1">*</span>
              )}
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={getDisplayText(reasonPlaceholderEs, reasonPlaceholderEn)}
              rows={3}
              className={cn(
                'w-full px-3 py-2 border border-gray-300 rounded-lg',
                'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                'resize-none'
              )}
              disabled={isProcessing}
            />
            {currentAction?.requiresReason && !reason.trim() && (
              <p className="text-xs text-red-600 mt-1">
                {locale === 'es' ? 'Este campo es requerido' : 'This field is required'}
              </p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            {/* Reset/Back button */}
            <button
              type="button"
              onClick={handleReset}
              disabled={isProcessing}
              className={cn(
                'inline-flex items-center px-4 py-2 text-sm font-medium',
                'text-gray-700 bg-white border border-gray-300 rounded-lg',
                'hover:bg-gray-50 hover:border-gray-400',
                'focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-1',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                'transition-all duration-200'
              )}
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              {locale === 'es' ? 'Volver' : 'Back'}
            </button>

            {/* Confirm button */}
            <button
              type="button"
              onClick={handleConfirm}
              disabled={isProcessing || (currentAction?.requiresReason && !reason.trim())}
              className={cn(
                'inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg',
                'focus:outline-none focus:ring-2 focus:ring-offset-1',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                'transition-all duration-200 flex-1 justify-center',
                
                // Match selected action variant
                currentAction?.variant === 'primary' && [
                  'bg-blue-600 text-white',
                  'hover:bg-blue-700 focus:ring-blue-500'
                ],
                currentAction?.variant === 'danger' && [
                  'bg-red-600 text-white',
                  'hover:bg-red-700 focus:ring-red-500'
                ],
                (!currentAction?.variant || currentAction?.variant === 'secondary') && [
                  'bg-gray-600 text-white',
                  'hover:bg-gray-700 focus:ring-gray-500'
                ]
              )}
            >
              {isProcessing ? (
                <>
                  <div className="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full mr-2" />
                  {locale === 'es' ? 'Procesando...' : 'Processing...'}
                </>
              ) : (
                <>
                  {currentAction?.icon && <currentAction.icon className="w-4 h-4 mr-2" />}
                  {getDisplayText(confirmButtonEs, confirmButtonEn)}
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  )
})

BirhausApprovalWorkflow.displayName = 'BirhausApprovalWorkflow'

// Hook for managing approval workflow state
export function useBirhausApprovalWorkflow<T>() {
  const [item, setItem] = useState<T | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const startApproval = useCallback((newItem: T) => {
    setItem(newItem)
    setError(null)
  }, [])

  const clearApproval = useCallback(() => {
    setItem(null)
    setError(null)
  }, [])

  const handleAction = useCallback(async (
    actionKey: string,
    approvalItem: T,
    reason?: string,
    actionHandler?: (action: string, item: T, reason?: string) => Promise<void>
  ) => {
    if (!actionHandler) return

    try {
      setLoading(true)
      setError(null)
      await actionHandler(actionKey, approvalItem, reason)
      clearApproval()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
      throw err
    } finally {
      setLoading(false)
    }
  }, [clearApproval])

  return {
    item,
    loading,
    error,
    startApproval,
    clearApproval,
    handleAction,
    setError
  }
}

// Common approval action presets
export const birhausApprovalActions = {
  // Standard approve/reject
  approveReject: [
    {
      key: 'approve',
      labelEs: 'Aprobar',
      labelEn: 'Approve',
      variant: 'primary' as const,
      icon: CheckCircle,
      confirmationMessageEs: 'Esta acción aprobará el elemento.',
      confirmationMessageEn: 'This action will approve the item.'
    },
    {
      key: 'reject',
      labelEs: 'Rechazar',
      labelEn: 'Reject',
      variant: 'danger' as const,
      icon: XCircle,
      requiresReason: true,
      confirmationMessageEs: 'Esta acción rechazará el elemento.',
      confirmationMessageEn: 'This action will reject the item.'
    }
  ],

  // Financial transaction approval
  financialApproval: [
    {
      key: 'approve',
      labelEs: 'Aprobar Transacción',
      labelEn: 'Approve Transaction',
      variant: 'primary' as const,
      icon: CheckCircle,
      confirmationMessageEs: 'Esta acción procesará la transacción y actualizará los registros financieros.',
      confirmationMessageEn: 'This action will process the transaction and update financial records.'
    },
    {
      key: 'reject',
      labelEs: 'Rechazar Transacción',
      labelEn: 'Reject Transaction',
      variant: 'danger' as const,
      icon: XCircle,
      requiresReason: true,
      confirmationMessageEs: 'Esta acción cancelará la transacción y notificará al solicitante.',
      confirmationMessageEn: 'This action will cancel the transaction and notify the requester.'
    }
  ],

  // Document approval
  documentApproval: [
    {
      key: 'approve',
      labelEs: 'Aprobar Documento',
      labelEn: 'Approve Document',
      variant: 'primary' as const,
      icon: CheckCircle,
      confirmationMessageEs: 'Esta acción marcará el documento como aprobado.',
      confirmationMessageEn: 'This action will mark the document as approved.'
    },
    {
      key: 'request_changes',
      labelEs: 'Solicitar Cambios',
      labelEn: 'Request Changes',
      variant: 'secondary' as const,
      requiresReason: true,
      confirmationMessageEs: 'Esta acción solicitará cambios en el documento.',
      confirmationMessageEn: 'This action will request changes to the document.'
    },
    {
      key: 'reject',
      labelEs: 'Rechazar Documento',
      labelEn: 'Reject Document',
      variant: 'danger' as const,
      icon: XCircle,
      requiresReason: true,
      confirmationMessageEs: 'Esta acción rechazará permanentemente el documento.',
      confirmationMessageEn: 'This action will permanently reject the document.'
    }
  ]
}